import type { TFunction } from 'i18next';
import { TEMPLATE_COLOR_PALETTE } from '../constants/templateColors';
import {
  SessionType,
  type CreateInterventionTemplateRequest,
  type InterventionTemplateResponse,
  type MandatoryStaffCompetenceRequirement,
  type UpdateInterventionTemplateRequest
} from '../api/generated';

export const DEFAULT_REQUIREMENT_COUNT = 1;
export const DEFAULT_PRE_PROCESSING_MINUTES = 0;
export const DEFAULT_POST_PROCESSING_MINUTES = 5;

// The same competence may appear in several mandatory rows (e.g. two nurses, one with
// preparation time). Rows therefore carry a client-only `rowId` so React keys and
// updates stay stable; it is stripped before the form is saved.
export type MandatoryRow = MandatoryStaffCompetenceRequirement & { rowId: string };

export type InterventionFormState = Omit<
  InterventionTemplateResponse,
  'mandatoryStaffCompetenceRequirements'
> & {
  mandatoryStaffCompetenceRequirements: MandatoryRow[];
};

export const emptyIntervention = (): InterventionTemplateResponse => ({
  id: crypto.randomUUID(),
  name: '',
  color: TEMPLATE_COLOR_PALETTE[0].strokeColor,
  // Interventions no longer carry a user-facing icon; kept undefined for payload shape.
  icon: undefined,
  durationMinutes: 30,
  sessionType: SessionType.Individual,
  mandatoryStaffCompetenceRequirements: [],
  flexStaffCompetenceRequirements: [],
  roomRequirements: []
});

// A row's professions are interchangeable: a staff member qualifies by holding any one of
// them. Blank ids count as no id at all, so a row of blanks reads as skill-less everywhere
// rather than rendering an entry with an empty name. Ids are de-duplicated to match the
// backend, which counts `.distinct()` when it checks a row is satisfiable: a row listing
// the same competence twice offers one competence, not two.
export const professionIds = (req: MandatoryStaffCompetenceRequirement): string[] =>
  Array.from(new Set((req.staffProfessionIds ?? []).filter((id) => id.trim() !== '')));

// Whether the form offers the distinctness checkbox. Below two staff the flag is genuinely
// inert and the algorithm ignores it; with a single profession it is not inert but
// impossible, so rather than let it be ticked the form leaves it off and
// `isDistinctnessSatisfiable` reports any such row that arrives from the server.
export const isDistinctnessApplicable = (req: MandatoryStaffCompetenceRequirement): boolean =>
  (req.count ?? DEFAULT_REQUIREMENT_COUNT) >= 2 && professionIds(req).length >= 2;

// Mirrors StaffCompetenceRequirementRules.requireSatisfiable on the backend, which refuses a
// row with a 400: with the flag set and more than one staff member, the row must name at
// least `count` different professions. Deliberately does NOT exempt the shapes the checkbox
// hides — one profession, or none — because those are exactly the rows the backend rejects,
// and `toFormState` can load one the form never authored.
export const isDistinctnessSatisfiable = (req: MandatoryStaffCompetenceRequirement): boolean =>
  !req.distinctCompetences ||
  (req.count ?? DEFAULT_REQUIREMENT_COUNT) <= 1 ||
  professionIds(req).length >= (req.count ?? DEFAULT_REQUIREMENT_COUNT);

// New form rows start at the defaults. An empty `staffProfessionIds` is the skill-less
// variant ("N staff, any competence"); it stays an explicit empty array in the payload.
export const mandatoryRow = (staffProfessionIds: string[]): MandatoryRow => ({
  rowId: crypto.randomUUID(),
  staffProfessionIds,
  count: DEFAULT_REQUIREMENT_COUNT,
  preProcessingMinutes: DEFAULT_PRE_PROCESSING_MINUTES,
  postProcessingMinutes: DEFAULT_POST_PROCESSING_MINUTES,
  distinctCompetences: false
});

// Applies an edit to a row and drops the distinctness flag as soon as it stops applying,
// so a row whose count was lowered or whose professions were removed never carries a flag
// the form no longer shows a checkbox for.
export const applyMandatoryRowPatch = (
  row: MandatoryRow,
  patch: Partial<MandatoryStaffCompetenceRequirement>
): MandatoryRow => {
  const next = { ...row, ...patch };
  return isDistinctnessApplicable(next) ? next : { ...next, distinctCompetences: false };
};

export const noSkillMandatoryRow = (): MandatoryRow => mandatoryRow([]);

export const toFormState = (iv: InterventionTemplateResponse): InterventionFormState => ({
  ...iv,
  mandatoryStaffCompetenceRequirements: iv.mandatoryStaffCompetenceRequirements.map((req) => ({
    ...req,
    rowId: crypto.randomUUID()
  }))
});

export const toInterventionResponse = (
  form: InterventionFormState
): InterventionTemplateResponse => ({
  ...form,
  mandatoryStaffCompetenceRequirements: form.mandatoryStaffCompetenceRequirements.map((row) => ({
    staffProfessionIds: row.staffProfessionIds,
    count: row.count,
    preProcessingMinutes: row.preProcessingMinutes,
    postProcessingMinutes: row.postProcessingMinutes,
    distinctCompetences: row.distinctCompetences ?? false
  }))
});

// Create and update accept the same payload shape; both mappers delegate here so the
// field-by-field mapping lives in one place.
const toTemplateRequestPayload = (
  iv: InterventionTemplateResponse
): CreateInterventionTemplateRequest => ({
  name: iv.name,
  color: iv.color,
  icon: iv.icon,
  durationMinutes: iv.durationMinutes,
  sessionType: iv.sessionType,
  roomRequirements: iv.roomRequirements,
  mandatoryStaffCompetenceRequirements: iv.mandatoryStaffCompetenceRequirements,
  flexStaffCompetenceRequirements: iv.flexStaffCompetenceRequirements
});

export const toCreateRequest = (
  iv: InterventionTemplateResponse
): CreateInterventionTemplateRequest => toTemplateRequestPayload(iv);

export const toUpdateRequest = (
  iv: InterventionTemplateResponse
): UpdateInterventionTemplateRequest => toTemplateRequestPayload(iv);

// The first numbered copy is "(kopi 2)": the unnumbered "(kopi)" is conceptually
// the first, so counting from 2 keeps the series readable rather than jumping
// from "(kopi)" to "(kopi 1)".
const FIRST_NUMBERED_COPY = 2;

// Names are compared trimmed and case-insensitively, so "Holdtræning (kopi)" and
// "holdtræning (KOPI)" count as taken by each other.
const normalizeName = (name: string): string => name.trim().toLocaleLowerCase();

// Builds the name for a duplicate: the source name with the copy suffix, counting up
// while that name is already in use.
//
// Uniqueness is enforced here because nothing else enforces it. The backend has no
// unique constraint on the name, and the treatment plan builder's add-intervention
// picker renders the name alone, so two identically named templates are
// indistinguishable at the moment of choosing one (REH-323).
//
// The suffix is appended to whatever the source is called rather than parsed out of
// it, so duplicating a copy yields "X (kopi) (kopi)". That is deliberate: an honest
// nesting beats guessing at what the admin meant by a name they typed themselves.
export const buildDuplicateName = (
  sourceName: string,
  existingNames: string[],
  t: TFunction
): string => {
  const taken = new Set(existingNames.map(normalizeName));
  const firstAttempt = t('config-duplicate-name', { name: sourceName });
  if (!taken.has(normalizeName(firstAttempt))) return firstAttempt;

  // Bounded by the number of existing names: every iteration rules one out, so the
  // first free suffix arrives within `taken.size + 1` steps.
  for (let n = FIRST_NUMBERED_COPY; n <= taken.size + FIRST_NUMBERED_COPY; n += 1) {
    const candidate = t('config-duplicate-name-n', { name: sourceName, n });
    if (!taken.has(normalizeName(candidate))) return candidate;
  }
  return firstAttempt;
};

// Competence ids the intervention requires that are absent from the account's live
// competence list. The list endpoint defaults to excluding archived entries and this
// project never asks for them, so an id missing from that list is an archived one.
//
// This matters for duplication: the backend validates archived professions on create
// as well as update, so a template created before a competence was archived keeps
// working while a copy of it would be rejected with a 409 Conflict.
//
// The caller must only trust this once the competence list has actually loaded; an
// empty list makes every referenced id look archived.
export const findArchivedCompetenceIds = (
  iv: InterventionTemplateResponse,
  liveCompetenceIds: Set<string>
): string[] => {
  const referenced = new Set<string>();
  iv.mandatoryStaffCompetenceRequirements.forEach((req) => {
    req.staffProfessionIds?.forEach((id) => {
      if (id.trim() !== '') referenced.add(id);
    });
  });
  iv.flexStaffCompetenceRequirements.forEach((req) => {
    if (req.staffProfessionId) referenced.add(req.staffProfessionId);
  });
  return Array.from(referenced).filter((id) => !liveCompetenceIds.has(id));
};

// Rows are summarised per profession set, so "Nurse" and "Nurse / Physiotherapist" stay
// apart while two plain "Nurse" rows merge. A distinct row is its own bucket too: its staff
// are not interchangeable with a plain row's. Skill-less rows share one labelled bucket.
const PROFESSION_SEPARATOR = ' / ';

interface RequiredCompetenceBucket {
  label: string;
  count: number;
}

const bucketLabel = (
  req: MandatoryStaffCompetenceRequirement,
  nameById: Map<string, string>,
  t: TFunction
): string => {
  const ids = professionIds(req);
  if (ids.length === 0) return t('config-iv-no-skill');
  const names = ids.map((id) => nameById.get(id) ?? id).join(PROFESSION_SEPARATOR);
  return req.distinctCompetences && isDistinctnessApplicable(req)
    ? `${names} (${t('config-iv-distinct-short')})`
    : names;
};

// The same requirement may appear in several rows; aggregate them into one "N× Label" entry.
export const formatRequiredCompetences = (
  iv: InterventionTemplateResponse,
  nameById: Map<string, string>,
  t: TFunction
): string => {
  const buckets = new Map<string, RequiredCompetenceBucket>();
  iv.mandatoryStaffCompetenceRequirements.forEach((req) => {
    const label = bucketLabel(req, nameById, t);
    const existing = buckets.get(label);
    const count = req.count ?? DEFAULT_REQUIREMENT_COUNT;
    buckets.set(label, { label, count: (existing?.count ?? 0) + count });
  });
  return Array.from(buckets.values())
    .map(({ label, count }) => `${count}× ${label}`)
    .join(', ');
};

export const formatFlexCompetences = (
  iv: InterventionTemplateResponse,
  nameById: Map<string, string>,
  t: TFunction
): string =>
  iv.flexStaffCompetenceRequirements
    .map((req) => {
      const id = req.staffProfessionId;
      if (!id) return null;
      const count = req.count ?? DEFAULT_REQUIREMENT_COUNT;
      return `+ ${nameById.get(id) ?? id} (${t('config-iv-flex-of-them', { n: count })})`;
    })
    .filter((line): line is string => line !== null)
    .join(', ');
